import { Grid, Typography } from "@mui/material";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import DialogContentWrapper from "../../../../../../common/components/functional/dialogs/DialogContentWrapper";
import { POSITIVE_INTEGER_REGEX } from "../../../../../../common/helpers/regexes";
import {
  PRICING_SPECIAL_PATH_START,
  PricingCategory,
  PricingSpecialSetting,
} from "../../../constants";
import LabelInputRow from "../../../form/LabelInputRow";
import LabelValueRow from "../../../form/LabelValueRow";
import { getPricingObjectForSpecialRulesAndPricing } from "../../../helpers/pricingUtils";
import { UnsavedChangesProvider } from "../../../../../../contexts/UnsavedChangesContext";
import { PricingGuideText } from "../../../styles";
import isEmpty from "../../../../../../common/helpers/isEmpty";
import { ModalState } from "../../../../types/types";
import { useState } from "react";
import DialogComponent from "../../../../../../common/components/functional/dialogs/DialogComponent";
import {
  EDIT_BUTTON_TEXT,
  getSpecialPricingByExpenseTypeModalContent,
  VIEW_BUTTON_TEXT,
} from "./helpers";

type Props = {
  tesId?: string;
  disabled?: boolean;
  onClose: () => void;
};

const MAIN_FORM_PROPERTY_KEY = `${PRICING_SPECIAL_PATH_START}.${PricingSpecialSetting.EarlyBirdOrder}`;
const DRAG_HANDLE_SELECTOR = "drag-handle-early-bird-rules-and-pricing";

const EarlyBirdOrderRulesAndPricingForm = ({
  tesId = "",
  disabled = false,
  onClose,
}: Props) => {
  const { t } = useTranslation("customers");
  const { getValues: getMainFormValues, setValue: setMainFormValue } =
    useFormContext();

  const mainFormValues = getMainFormValues(MAIN_FORM_PROPERTY_KEY);
  const days = mainFormValues?.settings?.days;
  const pricing = mainFormValues?.settings?.pricing;

  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const onOpenModal = (id?: string) => setModalState({ show: true, id });
  const onCloseModal = () =>
    setModalState((prevState) => ({ ...prevState, show: false }));
  const onModalExited = () => {
    setModalState({ show: false, id: "" });
  };

  const defaultValues = {
    days: days || "",
    baseHours: {
      multiplier: pricing?.baseHours?.multiplier?.value || "",
      expenses: pricing?.baseHours?.expenses,
    },
    supplements: {
      multiplier: pricing?.supplements?.multiplier?.value || "",
      expenses: pricing?.supplements?.expenses,
    },
  };

  const methods = useForm({
    defaultValues,
    mode: "onChange",
  });

  const {
    watch,
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onSubmit = (formData: any) => {
    const updatedPricingObject = {
      enabled: mainFormValues.enabled,
      settings: {
        days: formData.days,
        pricing: getPricingObjectForSpecialRulesAndPricing({
          formData,
        }),
      },
    };

    setMainFormValue(MAIN_FORM_PROPERTY_KEY, updatedPricingObject, {
      shouldDirty: true,
    });

    onClose();
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => onClose();

  const watchedDays = watch("days");
  const watchedBaseHours = watch("baseHours");
  const watchedSupplements = watch("supplements");

  const MAX_HOURS = 10000;
  const isDaysInputValid =
    POSITIVE_INTEGER_REGEX.test(watchedDays) && watchedDays <= MAX_HOURS;

  const byMultiplierIsValid =
    isDaysInputValid &&
    !!watchedBaseHours.multiplier &&
    !!watchedSupplements.multiplier;

  const showBaseHoursViewButton = !isEmpty(watchedBaseHours.expenses);
  const showSupplementsViewButton = !isEmpty(watchedSupplements.expenses);

  const saveDisabled = !isDirty || !byMultiplierIsValid;

  return (
    <DialogContentWrapper
      title={t("contract.earlyBirdOrderRulesAndPricing")}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled}
    >
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <PricingGuideText paddingTop="0">
            {t("contract.earlyBirdGuideText")}
          </PricingGuideText>
          <LabelInputRow
            label={t("contract.amountOfDays")}
            labelWidth={3}
            fieldName="days"
            inputType="number"
            inputWidth={3}
            inputSpec={t("contract.daysAlt")}
            inputError={!isDaysInputValid}
            disabled={disabled}
            decimalScale={3}
          />
          <Grid container sx={{ margin: "-0.5rem 0 1rem 1rem" }}>
            <Grid item xs={3} />
            <Grid item xs={9}>
              <Typography variant="secondary" fontSize="12px">
                {t("contract.dayRuleDescription")}
              </Typography>
            </Grid>
          </Grid>
          <LabelValueRow
            label={t("contract.pricingAlt")}
            labelWidth={3}
            value={t("contract.multiplier")}
            valueWidth={9}
          />
          <LabelInputRow
            label={t("settings:BaseHours")}
            labelWidth={3}
            fieldName="baseHours.multiplier"
            fieldType="number"
            inputWidth={3}
            inputSpec={t("contract.multiplier").toLowerCase()}
            inputError={!watchedBaseHours.multiplier}
            disabled={disabled}
            decimalScale={3}
            viewButtonText={VIEW_BUTTON_TEXT}
            editButtonText={EDIT_BUTTON_TEXT}
            onOpenModal={() => onOpenModal(PricingCategory.BaseHours)}
            showButton
            showViewButton={showBaseHoursViewButton}
            buttonWidth={5}
          />
          <LabelInputRow
            label={t("settings:Supplements")}
            labelWidth={3}
            fieldName="supplements.multiplier"
            fieldType="number"
            inputWidth={3}
            inputSpec={t("contract.multiplier").toLowerCase()}
            inputError={!watchedSupplements.multiplier}
            disabled={disabled}
            decimalScale={3}
            viewButtonText={VIEW_BUTTON_TEXT}
            editButtonText={EDIT_BUTTON_TEXT}
            onOpenModal={() => onOpenModal(PricingCategory.Supplements)}
            showButton
            showViewButton={showSupplementsViewButton}
            buttonWidth={5}
          />
          <DialogComponent
            open={modalState.show}
            onClose={onCloseModal}
            sx={{ display: "flex", maxHeight: "80%" }}
            dragHandleSelector={DRAG_HANDLE_SELECTOR}
            TransitionProps={{ onExited: onModalExited }}
          >
            {getSpecialPricingByExpenseTypeModalContent(
              modalState,
              tesId,
              onCloseModal,
              disabled,
              true,
              DRAG_HANDLE_SELECTOR,
            )}
          </DialogComponent>
        </UnsavedChangesProvider>
      </FormProvider>
    </DialogContentWrapper>
  );
};

export default EarlyBirdOrderRulesAndPricingForm;
